/**
 * BDM Service
 * Direct Database persistence: Uses SupabaseAdapter to communicate with remote DB.
 * Removed HybridPersistence for better data integrity and simplicity.
 */

import { SupabaseAdapter } from '@/lib/persistence/supabaseAdapter';
import { supabase } from '@/integrations/supabase/client';
import type { BdmInfo } from '@/types/quotation';
import type { Database } from '@/integrations/supabase/types';
import { normalizeTextValue } from '@/utils/textNormalization';
import { tenantService } from './tenantService';

// Database-aligned type
export type BdmDB = Database['public']['Tables']['bdms']['Row'];

// Create direct Supabase adapter
const bdmAdapter = new SupabaseAdapter<BdmDB>('bdms');

/**
 * Convert database format to application BdmInfo format
 */
const mapToAppFormat = (db: BdmDB): BdmInfo => ({
  id: db.id,
  name: db.name,
  email: db.email || undefined,
  phone: db.phone || undefined,
});

/**
 * Convert application format to database format
 */
const mapToDbFormat = (bdm: Omit<BdmInfo, 'id'>): Omit<BdmDB, 'id' | 'created_at' | 'updated_at'> => ({
  name: normalizeTextValue(bdm.name, 'name'),
  email: normalizeTextValue(bdm.email || '', 'email') || null,
  phone: normalizeTextValue(bdm.phone || '', 'phone') || null,
});

class BdmService {

  /**
   * Get all BDMs
   */
  async getBdms(): Promise<BdmInfo[]> {
    try {
      const tenant = await tenantService.getCurrentTenant();
      if (!tenant) return [];

      const { data, error } = await supabase
        .from('bdms')
        .select('*')
        .eq('tenant_id', tenant.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(mapToAppFormat);
    } catch (e) {
      console.error('[BDM Service] Primary fetch failed, trying adapter...', e);
      const result = await bdmAdapter.getAll();
      if (!result.success) {
        throw new Error(`BDM Fetch Critical Failure: ${result.error}`);
      }
      return (result.data || []).map(mapToAppFormat);
    }
  }

  /**
   * Get BDM by ID
   */
  async getBdm(id: string): Promise<BdmInfo | null> {
    const result = await bdmAdapter.getById(id);
    return result.success && result.data ? mapToAppFormat(result.data) : null;
  }

  /**
   * Add new BDM
   */
  async addBdm(bdm: Omit<BdmInfo, 'id'>): Promise<BdmInfo> {
    const tenant = await tenantService.getCurrentTenant();
    if (!tenant) throw new Error('No tenant found');
    
    const dbData = {
      ...mapToDbFormat(bdm),
      tenant_id: tenant.id
    };
    const result = await bdmAdapter.create(dbData as unknown);
    if (!result.success || !result.data) throw new Error(`Failed to save BDM: ${result.error}`);
    return mapToAppFormat(result.data);
  }

  /**
   * Update BDM
   */
  async updateBdm(id: string, updates: Partial<BdmInfo>): Promise<BdmInfo> {
    const dbUpdates: Partial<BdmDB> = {
    };
    if (updates.name !== undefined) dbUpdates.name = normalizeTextValue(updates.name, 'name');
    if (updates.email !== undefined) dbUpdates.email = normalizeTextValue(updates.email || '', 'email') || null;
    if (updates.phone !== undefined) dbUpdates.phone = normalizeTextValue(updates.phone || '', 'phone') || null;

    const result = await bdmAdapter.update(id, dbUpdates as unknown);
    if (!result.success || !result.data) throw new Error(`BDM Update Failed: ${result.error}`);
    return mapToAppFormat(result.data);
  }

  /**
   * Delete BDM
   */
  async deleteBdm(id: string): Promise<boolean> {
    const result = await bdmAdapter.delete(id);
    return result.success;
  }

  /**
   * Compatibility Methods (Reduced to NOP or direct)
   */
  async saveDraft(id: string): Promise<boolean> { return true; }
  isSaved(id: string): boolean { return true; }
  getUnsavedBdms(): BdmInfo[] { return []; }
  async syncAll(): Promise<boolean> { return true; }
  hasUnsavedChanges(): boolean { return false; }
  async refresh(): Promise<BdmInfo[]> { return this.getBdms(); }
}

export const bdmService = new BdmService();
